<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faBagShopping,
    faBell,
    faBellPlus,
} from '@fortawesome/pro-regular-svg-icons';
import CircleWaves from '@components/ui/hotelPage&view/CircleWaves.vue';
import { PriceTrackType } from '@store/priceTrack';
import { notifyTrack } from '@composable/usePriceTrackModal';

import { MrSwitch } from '#components';
import type TripModule from '@itinerary/trip/TripModule';
import { type TripSearchForm } from '@search/trip/TripSearchForm';

interface IProps {
    inner?: boolean;
    mobileOnly?: boolean;
    desktopOnly?: boolean;
    forPreSell?: boolean;
}

const { inner, mobileOnly, desktopOnly, forPreSell } = defineProps<IProps>();

const emit = defineEmits<{
    'toggle:restart': [];
}>();

const priceTrackStore = usePriceTrackStore();
const flightForm = useFlightForm();
const { isCapacityTrackCampaign } = moduleToRefs(flightForm);
const analyticsStore = useAnalyticsStore();
const layoutStore = useLayoutStore();
const itineraryStore = useItineraryStore();
const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
const { originCode, destinationCode, departureDate } = moduleToRefs(
    currentSearchForm.value
);
const { haveTracking } = storeToRefs(itineraryStore);
const currentModule = useCurrentItineraryModule<TripModule>();
const { areAllPackagesFull, originCurrentCode, destinationCurrentCode } =
    moduleToRefs(currentModule.value);
const toggle = ref<InstanceType<typeof MrSwitch>>();
const rootEl = ref<HTMLDivElement>();
const { isMobile } = useSize();
const show = computed(() => {
    if (isCapacityTrackCampaign.value) {
        return areAllPackagesFull.value;
    }
    return haveTracking.value;
});
const trackingType = computed<
    | {
          icon: IconDefinition;
          iconColor?: string;
          type?: PriceTrackType;
          title?: string;
          description?: string;
      }
    | undefined
>(() => {
    if (forPreSell) {
        return {
            type: PriceTrackType.PreSell,
            title: 'اطلاع از پیش‌فروش قطار',
            icon: faBagShopping,
        };
    }
    if (areAllPackagesFull.value) {
        return {
            type: PriceTrackType.Capacity,
            title: 'موجود شد خبرم کن',
            icon: faBellPlus,
            iconColor: 'var(--blue-400)',
        };
    }
    return {
        type: PriceTrackType.Price,
        title: 'ارزان شد خبرم کن',
        icon: faBell,
        iconColor: 'var(--blue-400)',
    };
});
const selected = computed({
    get(): boolean {
        return !!priceTrack.value;
    },
    set(_: boolean) {
        addPriceTrack();
    },
});
const priceTrack = computed(() => {
    if (trackingType.value?.type === PriceTrackType.PreSell) {
        const prevList = priceTrackStore.trainPreSellAlerts;
        return prevList.includes(
            `${originCode.value}-${destinationCode.value}`
        );
    } else {
        return priceTrackStore.priceTrackList.find(
            p =>
                !p.serviceId &&
                !p.serviceNumber &&
                p.originCode === originCurrentCode?.value &&
                p.destinationCode === destinationCurrentCode.value &&
                p.notify &&
                p.date &&
                departureDate.value &&
                dayConfig.gregory(p.date).isSame(departureDate.value, 'day')
        );
    }
});
const guideStepText = computed(() => {
    if (forPreSell) {
        return 'با روشن‌کردن این گزینه، درصورت پیش‌فروش این مسیر به شما اطلاع‌رسانی خواهد شد.';
    }
    if (areAllPackagesFull.value) {
        return 'با روشن‌کردن این گزینه، درصورت موجود شدن ظرفیت این مسیر به شما اطلاع‌رسانی خواهد شد.';
    }
    return 'با روشن‌کردن این گزینه، درصورت ارزان شدن این مسیر به شما اطلاع‌رسانی خواهد شد.';
});
const guideStepTitle = computed(() => {
    if (forPreSell) {
        return 'اطلاع پیش‌فروش';
    }
    if (areAllPackagesFull.value) {
        return 'رهگیری ظرفیت';
    }
    return 'رهگیری قیمت';
});
const removeTour = () =>
    layoutStore.capacityTrackTour.removeStep(layoutStore.pathTrackingStepKey);
const addTourStep = () => {
    if (mobileOnly && !isMobile.value) {
        return;
    }
    if (desktopOnly && isMobile.value) {
        return;
    }
    const { complete } = layoutStore.capacityTrackTour;
    nextTick(() => {
        layoutStore.capacityTourAddStep({
            step: {
                id: layoutStore.pathTrackingStepKey,
                title: guideStepTitle.value,
                text: guideStepText.value,
                attachTo: {
                    element: rootEl.value!,
                    on: isMobile.value ? 'bottom' : 'left',
                },
                buttons: [
                    {
                        text: 'تمام',
                        action: complete,
                    },
                ],
            },
            index: 1,
        });
    });
};
onClickOutside(rootEl, evt => {
    const target = evt.target as SVGPathElement;
    if (
        layoutStore.capacityTrackTour.getCurrentStep()?.id ===
            layoutStore.pathTrackingStepKey &&
        target?.parentElement?.classList?.contains(
            'shepherd-modal-overlay-container'
        )
    ) {
        layoutStore.capacityTrackTour.complete();
    }
});
const addPriceTrack = async () => {
    analyticsStore.trackPosthog(
        `click-on-add-price-track-${trackingType.value?.type}`
    );
    await notifyTrack({
        type: trackingType.value?.type,
    }).then(() => restartToggle());
};
const restartToggle = () => {
    toggle.value?.restart();
    emit('toggle:restart');
};
watch(show, val => {
    if (val) {
        addTourStep();
    } else {
        removeTour();
    }
});
onMounted(() => {
    if (show.value) {
        addTourStep();
    }
});
onBeforeUnmount(() => removeTour());
</script>

<template>
    <div v-show="show" ref="rootEl" class="w-full">
        <CircleWaves class="waves" to-scale="scale(1.2, 1.5)" />
        <div
            class="price-track"
            :class="[{ selected, inner }]"
            @click="() => (selected = !selected)"
        >
            <div class="root">
                <mr-icon
                    v-if="trackingType"
                    class="icon"
                    :icon="trackingType.icon"
                    :color="trackingType.iconColor"
                />
                <div
                    class="title"
                    :class="{ described: !!trackingType?.description }"
                >
                    {{ trackingType?.title }}
                </div>
                <p v-if="trackingType?.description" class="desktop-only">
                    {{ trackingType.description }}
                </p>
                <mr-switch
                    ref="toggle"
                    v-model="selected"
                    size="big"
                    @click.stop
                />
            </div>
            <p v-if="trackingType?.description" class="mobile-only">
                {{ trackingType.description }}
            </p>
        </div>
    </div>
</template>
<style lang="scss" scoped>
.w-full {
    width: 100%;
    position: relative;
}

.waves {
    display: none;
}

// eslint-disable-next-line vue-scoped-css/no-unused-selector, vue-scoped-css/require-selector-used-inside
.shepherd-enabled.shepherd-target {
    .waves {
        display: flex;
    }
}
// eslint-disable vue-scoped-css/require-selector-used-inside, vue-scoped-css/no-unused-selector
.price-track {
    width: 100%;
    border-radius: 12px;
    @include shadow();
    padding: 12px 16px;
    z-index: 100;
    background-color: white;
    &.inner {
        @include shadow(0);
    }
    > p {
        margin-top: 16px;
        margin-bottom: 0;
        @include tg-s($w5);
    }
    .root {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        width: 100%;
        height: 100%;
        @include mobile-only() {
            flex-wrap: nowrap;
        }
        .title {
            @include tg-m($w7, usePalette());
            white-space: nowrap;
            margin: 0 8px 0 12px;
            flex-grow: 1;
            &.icon {
                flex-grow: unset;
            }
            &.described {
                @include desktop-only {
                    flex-grow: unset;
                }
            }
        }
        p {
            @include tg-s($w5);
            flex-grow: 1;
            margin: 0;
            &.desktop {
                visibility: visible;
                @include mobile-only {
                    visibility: hidden;
                    height: 0;
                }
            }
        }
    }
    &.notifier {
        span,
        p {
            color: usePalette($p-purple, $p-700);
        }
    }
}
</style>
